import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/products").then((r) => {
    const prods = r.data.filter((p) => p.type === "product");
    setProducts(prods);
    setCounts(Object.fromEntries(prods.map((p) => [p.id, ""])));
  }), []);
  useEffect(() => { load(); }, [load]);

  const filtered = products.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return p.name.toLowerCase().includes(s) || (p.barcode || "").includes(q) || (p.category || "").toLowerCase().includes(s);
  });

  const changed = Object.entries(counts).filter(([id, v]) => v !== "" && Number(v) !== (products.find((p) => p.id === id)?.stock || 0));

  const save = async () => {
    if (!changed.length) return toast.info("Nenhuma alteração para salvar");
    if (!confirm(`Salvar inventário? ${changed.length} produto(s) terão o estoque ajustado.`)) return;
    setSaving(true);
    try {
      const payload = changed.map(([id, v]) => ({ product_id: id, counted: Number(v) }));
      const { data } = await api.post("/inventory/count", payload);
      toast.success(`Inventário salvo: ${data.adjusted} ajuste(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Estoque</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Inventário Físico</h1>
          <p className="text-sm text-slate-500 mt-1">Preencha a quantidade contada em cada produto. Deixe em branco para não alterar.</p>
        </div>
        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" disabled={saving || !changed.length} data-testid="save-inventory-btn">
          <Save className="w-4 h-4 mr-2" /> Salvar Contagem ({changed.length})
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Buscar produto / categoria / código..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" />
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Produto</TableHead><TableHead>Categoria</TableHead>
              <TableHead>Código</TableHead>
              <TableHead className="text-right">Sistema</TableHead>
              <TableHead className="text-right">Contado</TableHead>
              <TableHead className="text-right">Diferença</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const c = counts[p.id];
                const diff = c === "" ? null : Number(c) - p.stock;
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">{p.category || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{p.barcode || "—"}</TableCell>
                    <TableCell className="text-right kpi-num">{p.stock}</TableCell>
                    <TableCell className="text-right">
                      <Input type="number" value={c} onChange={(e) => setCounts({ ...counts, [p.id]: e.target.value })}
                        className="h-8 w-24 ml-auto text-right" placeholder="—" data-testid={`inv-count-${p.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      {diff === null ? <span className="text-slate-300">—</span> :
                        diff === 0 ? <Badge className="bg-slate-500">OK</Badge> :
                        diff > 0 ? <Badge className="bg-emerald-600">+{diff}</Badge> :
                        <Badge variant="destructive">{diff}</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
